/**
 * utils.js: shared REST + comment helpers exposed as `window.scrollToUtils`.
 *
 * Consumed by every other front-end module (ui.js, social.js, nav.js,
 * feed.js, single.js, single-player.js, search-palette.js). Loads first in
 * the dependency graph so `wp_localize_script` payloads (`scrollToData`,
 * `scrollToConst`) are attached to this file's tag and visible to every
 * downstream module.
 *
 * @file
 */

window.scrollToUtils = (function () {
  'use strict';

  const C                 = window.scrollToConst || {};
  const FEEDBACK_RESET_MS = C.feedbackResetMs ?? 2500;

  /**
   * Insert server-rendered comment HTML into a list element.
   *
   * @param {HTMLElement} list     The `<ol>` or `<ul>` comment list container.
   * @param {string}      html     Raw HTML fragment to parse and insert.
   * @param {boolean}     prepend  Insert at the top when true, append otherwise.
   * @returns {void}
   */
  function insertCommentHtml(list, html, prepend) {
    const range = document.createRange();
    range.selectNode(list);
    const frag = range.createContextualFragment(html);
    if (prepend) { list.prepend(frag); } else { list.append(frag); }
  }

  /**
   * Temporarily replace an input's placeholder with a feedback message.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement} input    Field whose placeholder is swapped.
   * @param {string}                               message  Transient placeholder text.
   * @returns {void}
   */
  function tempPlaceholder(input, message) {
    const prev = input.placeholder;
    input.placeholder = message;
    setTimeout(() => { input.placeholder = prev; }, FEEDBACK_RESET_MS);
  }

  /**
   * Build a fully-qualified REST URL from a relative path.
   *
   * Falls back to `/wp-json/scroll-to/v1` if `scrollToData.restRoot` is missing.
   *
   * @param {string} path  Endpoint path, with or without leading slash.
   * @returns {string} Absolute REST URL.
   */
  function restUrl(path) {
    const root = (window.scrollToData || {}).restRoot || '/wp-json/scroll-to/v1';
    return root.replace(/\/$/, '') + (path.startsWith('/') ? path : '/' + path);
  }

  /**
   * Send a POST to a REST endpoint with the wp_rest nonce header attached.
   *
   * Accepts either a `FormData` body (used as-is, nonce mirrored into the
   * payload) or any JSON-serialisable value (encoded as `application/json`).
   *
   * @param {string}              path   REST path (relative to scroll-to namespace).
   * @param {FormData|object}     [body] Optional request body.
   * @returns {Promise<object>} Parsed JSON response or `{ code, status }` on parse failure.
   */
  async function restPost(path, body) {
    const nonce = (window.scrollToData || {}).restNonce || '';
    const init  = { method: 'POST', headers: { 'X-WP-Nonce': nonce } };

    if (body instanceof FormData) {
      if (nonce && !body.has('_wpnonce')) body.set('_wpnonce', nonce);
      init.body = body;
    } else if (body) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    const res = await fetch(restUrl(path), init);
    try {
      return await res.json();
    } catch (_) {
      return { code: 'parse_error', status: res.status };
    }
  }

  /**
   * GET a REST endpoint with optional query params and the wp_rest nonce.
   *
   * @param {string}              path    REST path (relative to scroll-to namespace).
   * @param {object|URLSearchParams} [query] Optional query params.
   * @returns {Promise<object>} Parsed JSON response or `{ code, status }` on parse failure.
   */
  async function restGet(path, query) {
    const nonce = (window.scrollToData || {}).restNonce || '';
    const qs    = query ? '?' + new URLSearchParams(query).toString() : '';
    const res   = await fetch(restUrl(path) + qs, { headers: { 'X-WP-Nonce': nonce } });
    try {
      return await res.json();
    } catch (_) {
      return { code: 'parse_error', status: res.status };
    }
  }

  /**
   * Submit a comment form to /post/{id}/comments and dispatch outcome callbacks.
   *
   * Owns the cross-surface concerns: disables the submit button before the
   * REST call, re-enables it in `finally`, branches on `data.code` to fire
   * the error callback, and routes a successful response through
   * `onResult`. Surface-specific concerns (input clearing, DOM insertion,
   * counter updates, reply-form reset, logged-out redirect, value trim)
   * stay with the caller.
   *
   * Error display priority (rate limit, validation, network throw):
   *   1. `errorEl` (if supplied): populated with the server message and
   *      revealed via `hidden = false`. Visible regardless of input state.
   *   2. `feedbackEl` (fallback): placeholder swap via {@link tempPlaceholder}.
   *      Only visible when the input is empty (works for the feed quick-comment
   *      input that clears on success; useless on a textarea that keeps the
   *      user's typed content). Provide `errorEl` whenever the form has a
   *      textarea or any field that retains its value on error.
   *
   * The success callback receives the parsed envelope; `data.approved === 0`
   * signals held-for-moderation.
   *
   * @param {object}             opts
   * @param {HTMLFormElement}    opts.form        Form being submitted (used to find the submit button).
   * @param {string|number}      opts.postId      Post ID for the REST path.
   * @param {FormData|object}    opts.body        Request body forwarded to {@link restPost}.
   * @param {HTMLElement}        [opts.errorEl]   Inline error banner, receives server message, revealed via `hidden=false`.
   * @param {HTMLElement}        [opts.feedbackEl] Fallback input/textarea for placeholder feedback when `errorEl` is omitted.
   * @param {Function}           [opts.onResult]  Called with the parsed envelope when the server accepts the comment.
   * @param {Function}           [opts.onError]   Called with the parsed envelope (or null on throw) on failure.
   * @returns {Promise<void>}
   */
  async function submitComment(opts) {
    const { form, postId, body, errorEl, feedbackEl, onResult, onError } = opts;
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;

    // Clear any prior error banner so the user knows the new submit is in flight.
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const showError = (msg) => {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      } else if (feedbackEl) {
        tempPlaceholder(feedbackEl, msg);
      }
    };

    try {
      const data = await restPost(`/post/${postId}/comments`, body);
      if (!data || data.code) {
        showError((data && data.message) || 'Comment could not be posted.');
        onError?.(data);
        return;
      }
      onResult?.(data);
    } catch (err) {
      console.error('Comment post failed', err);
      showError('Something went wrong.');
      onError?.(null);
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  return { restPost, restGet, restUrl, FEEDBACK_RESET_MS, insertCommentHtml, tempPlaceholder, submitComment };
})();

;
/**
 * core/ui.js: sidebar drawer, scroll-to-top, header search, ring rotator,
 * mobile menu proxy.
 *
 * Reads `window.scrollToConst` for tuning constants (breakpoint, transition,
 * threshold, rotator step). No REST or `scrollToData` dependency.
 *
 * @file
 */

(function () {
  'use strict';

  const C                       = window.scrollToConst || {};
  const SIDEBAR_TRANSITION_MS   = C.sidebarTransitionMs   ?? 320;
  const SCROLL_TOP_THRESHOLD    = C.scrollTopThreshold    ?? 400;
  const MOBILE_BREAKPOINT_PX    = C.mobileBreakpointPx    ?? 768;
  const RING_ROTATOR_INTERVAL_MS = C.ringRotatorIntervalMs ?? 60;
  const RING_ROTATOR_STEP_DEG    = C.ringRotatorStepDeg    ?? 0.3;

  /**
   * Sidebar drawer: handles open/close, scroll-lock, backdrop, ESC, resize.
   */
  (function Sidebar() {
    const sidebar  = document.getElementById('sidebar');
    const toggle   = document.getElementById('sidebar-toggle');
    const closeBtn = document.getElementById('sidebar-close');
    const backdrop = document.getElementById('sidebar-backdrop');
    if (!sidebar || !toggle) return;

    let scrollY = 0;
    function show() {
      sidebar.classList.remove('hidden');
      requestAnimationFrame(() => {
        sidebar.classList.add('is-open');
        if (backdrop) backdrop.classList.add('is-shown');
      });
      toggle.setAttribute('aria-expanded', 'true');
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top      = `-${scrollY}px`;
      document.body.style.width    = '100%';
    }
    function hide() {
      sidebar.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-shown');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.width    = '';
      window.scrollTo(0, scrollY);
      if (window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches) {
        setTimeout(() => sidebar.classList.add('hidden'), SIDEBAR_TRANSITION_MS);
      }
    }
    const isOpen = () => sidebar.classList.contains('is-open');

    toggle.addEventListener('click', () => isOpen() ? hide() : show());
    if (closeBtn)  closeBtn.addEventListener('click', hide);
    if (backdrop)  backdrop.addEventListener('click', hide);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && isOpen()) hide(); });
    window.addEventListener('resize', () => {
      if (window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT_PX}px)`).matches) {
        sidebar.classList.remove('hidden', 'is-open');
        if (backdrop) backdrop.classList.remove('is-shown');
        document.body.style.position = '';
        document.body.style.top      = '';
        document.body.style.width    = '';
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  })();

  /**
   * Scroll-to-top floating button: fades in past threshold, smooth scrolls.
   */
  (function ScrollTop() {
    const btn = document.getElementById('scroll-top');
    if (!btn) return;
    const update = () => btn.classList.toggle('is-visible', window.scrollY > SCROLL_TOP_THRESHOLD);
    window.addEventListener('scroll', update, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    update();
  })();

  /**
   * Header search: swaps placeholder text between mobile/desktop breakpoints.
   */
  (function HeaderSearch() {
    const input = document.querySelector('.scroll-header-search');
    if (!input) return;
    const full   = input.placeholder;
    const mobile = '…';
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    function update(matches) { input.placeholder = matches ? mobile : full; }
    update(mq.matches);
    mq.addEventListener('change', e => update(e.matches));
  })();

  /**
   * Ring rotator: slowly drifts the gradient ring hue on every `.video-card`.
   *
   * Throttled to RING_ROTATOR_INTERVAL_MS via requestAnimationFrame so the
   * loop pauses when the tab is hidden and respects `prefers-reduced-motion`.
   */
  (function RingRotator() {
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let tick = 0;
    let last = 0;

    function step(now) {
      if (now - last >= RING_ROTATOR_INTERVAL_MS) {
        last = now;
        tick = (tick + RING_ROTATOR_STEP_DEG) % 360;
        document.querySelectorAll('.video-card').forEach(card => {
          const base = parseFloat(card.dataset.ringBase || '0');
          card.style.setProperty('--ring-from', (tick + base) % 360 + 'deg');
        });
      }
      requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  })();

  /**
   * Mobile menu button: proxies clicks on `[data-action="open-sidebar"]`
   * to the real sidebar-toggle button.
   */
  (function MobileMenuButton() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-action="open-sidebar"]')) return;
      document.getElementById('sidebar-toggle')?.click();
    });
  })();
})();

;
/**
 * core/search.js: pretty-search redirect + Cmd/Ctrl+K focus hotkey.
 *
 * Reads `window.scrollToData.searchUrl` for the pretty-search prefix.
 *
 * @file
 */

(function () {
  'use strict';

  const config = window.scrollToData || {};

  /**
   * Pretty-search redirect: rewrites `?s=` submissions to /search/{term}/.
   */
  (function PrettySearch() {
    document.querySelectorAll('form[role="search"], form:has(input[name="s"])').forEach(form => {
      form.addEventListener('submit', function (e) {
        const input = this.querySelector('input[name="s"]');
        if (!input) return;
        const term = input.value.trim();
        if (!term) return;
        e.preventDefault();
        window.location.href = (config.searchUrl || '/search/') + encodeURIComponent(term) + '/';
      });
    });
  })();

  /**
   * Cmd/Ctrl+K hotkey: focuses the first search input on the page.
   */
  (function SearchHotkey() {
    document.addEventListener('keydown', e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        document.querySelector('input[type="search"]')?.focus();
      }
    });
  })();
})();

;
/**
 * core/social.js: post-like + comment-like + native share.
 *
 * Consumes `window.scrollToUtils` (REST helpers) and `window.scrollToData`
 * (login URL, isLoggedIn, postId, liked state). Constants from
 * `window.scrollToConst`.
 *
 * @file
 */

(function () {
  'use strict';

  const config                 = window.scrollToData  || {};
  const C                      = window.scrollToConst || {};
  const SHARE_ICON_FEEDBACK_MS = C.shareIconFeedbackMs ?? 1500;

  /**
   * Post like/unlike: bootstraps current state, then toggles via REST.
   *
   * Adds `is-liked` / `text-bubblegum` classes, swaps Font Awesome
   * regular/solid heart icons, and live-updates any matching
   * `.scroll-like-count` element on success.
   */
  (function Likes() {
    document.querySelectorAll('.scroll-like-post').forEach(btn => {
      if (config.postId && btn.dataset.postId === String(config.postId) && config.liked) {
        btn.classList.add('is-liked', 'text-bubblegum');
        btn.querySelectorAll('i').forEach(i => i.classList.replace('fa-regular', 'fa-solid'));
      }

      btn.addEventListener('click', async function () {
        const postId = this.dataset.postId;
        try {
          const data = await window.scrollToUtils.restPost(`/post/${postId}/like`);
          if (!data || data.code) return;

          const liked = data.liked;
          this.classList.toggle('is-liked', liked);
          this.classList.toggle('text-bubblegum', liked);
          this.querySelectorAll('i').forEach(i => {
            i.classList.toggle('fa-solid', liked);
            i.classList.toggle('fa-regular', !liked);
          });

          document.querySelectorAll(`.scroll-like-count[data-post-id="${postId}"]`).forEach(el => {
            el.textContent = data.likes;
          });
        } catch (err) {
          console.error('Like failed', err);
        }
      });
    });
  })();

  /**
   * Comment like/unlike: delegated click handler that POSTs to REST.
   *
   * Redirects logged-out visitors to wp-login with a redirect-back hint.
   */
  (function CommentLikes() {
    document.addEventListener('click', async function (e) {
      const btn = e.target.closest('.scroll-like-comment');
      if (!btn) return;
      if (!config.isLoggedIn) {
        window.location.href = (config.loginUrl || '/wp-login.php') + '?redirect_to=' + encodeURIComponent(window.location.href);
        return;
      }
      const commentId = btn.dataset.commentId;
      try {
        const data = await window.scrollToUtils.restPost(`/comment/${commentId}/like`);
        if (!data || data.code) return;
        const countEl = btn.querySelector('.like-count');
        if (countEl) countEl.textContent = data.likes;
        btn.classList.toggle('is-liked', data.liked);
      } catch (err) {
        console.error('Comment like failed', err);
      }
    });
  })();

  /**
   * Share button: invokes the Web Share API or falls back to clipboard copy.
   *
   * On clipboard fallback, briefly swaps the icon to a checkmark.
   */
  (function Share() {
    function flashCheckIcon(iconEl) {
      if (!iconEl) return;
      const prev = iconEl.className;
      iconEl.className = 'fa-solid fa-check text-xl sm:text-2xl';
      setTimeout(() => { iconEl.className = prev; }, SHARE_ICON_FEEDBACK_MS);
    }

    function fallbackCopy(url, iconEl) {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(url).then(() => flashCheckIcon(iconEl));
    }

    document.querySelectorAll('.scroll-share').forEach(btn => {
      const iconEl = btn.querySelector('i');
      btn.addEventListener('click', function () {
        const url   = this.dataset.url   || window.location.href;
        const title = this.dataset.title || document.title;
        if (navigator.share) {
          navigator.share({ title, url }).catch(() => {});
        } else {
          fallbackCopy(url, iconEl);
        }
      });
    });
  })();
})();

;
/**
 * core/nav.js: sort-chip keyboard navigation, pagination shortcuts, layout
 * toggle (grid ⇄ feed via REST).
 *
 * Consumes `window.scrollToUtils` (restGet) and `window.scrollToData`
 * (hasToggle, layoutStorageKey).
 *
 * @file
 */

(function () {
  'use strict';

  const config = window.scrollToData || {};

  /**
   * Sort chips: arrow-key navigation within a `[role="group"]` of chips.
   */
  (function SortChipsNav() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' &&
          e.key !== 'ArrowDown'  && e.key !== 'ArrowUp') return;
      const chip = e.target.closest('.scroll-sort-chip');
      if (!chip) return;
      e.preventDefault();
      const group = chip.closest('[role="group"]');
      const chips = Array.from((group || document).querySelectorAll('.scroll-sort-chip'));
      const idx   = chips.indexOf(chip);
      const next  = (e.key === 'ArrowRight' || e.key === 'ArrowDown')
        ? chips[(idx + 1) % chips.length]
        : chips[(idx - 1 + chips.length) % chips.length];
      if (next) next.focus();
    });
  })();

  /**
   * Pagination keyboard shortcuts: left = previous page, right = next page.
   *
   * Skips when typing into an editable element or focused inside a sort chip.
   * Targets links rendered with `data-pagination="prev"|"next"`.
   */
  (function PaginationNav() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

      const tag = (document.activeElement || document.body).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.activeElement && document.activeElement.isContentEditable) return;
      if (document.activeElement && document.activeElement.closest('.scroll-sort-chip')) return;

      // Suppress prev/next-post nav while the gallery lightbox is open so
      // its own ←/→ image-paging takes over. The lightbox JS adds the
      // `scroll-lb-open` class to <body> on open and removes it on close,
      // so this gate is automatic, no cross-script coupling beyond the
      // CSS class agreement.
      if (document.body.classList.contains('scroll-lb-open')) return;

      const dir  = e.key === 'ArrowRight' ? 'next' : 'prev';
      const link = document.querySelector('[data-pagination="' + dir + '"]');
      if (!link) return;

      e.preventDefault();
      link.click();
    });
  })();

  /**
   * Layout toggle: swaps grid/feed view via REST without a full reload.
   *
   * Persists the choice in `localStorage` AND a same-site cookie so the
   * SSR pass on the next request can render the chosen layout up front.
   */
  (function LayoutToggle() {
    if (!config.hasToggle) return;

    const STORE_KEY = config.layoutStorageKey || 'scroll_layout_view';
    const region    = document.getElementById('scroll-layout-region');
    if (!region) return;

    // Restore saved preference (Safari private mode / quota-exceeded throws).
    let saved = null;
    try { saved = localStorage.getItem(STORE_KEY); } catch (_) {}
    if (saved && saved !== region.dataset.layout) {
      swap(saved, false);
    }

    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.scroll-layout-toggle');
      if (!btn || !region.contains(btn)) return;
      const next = region.dataset.layout === 'grid' ? 'feed' : 'grid';
      swap(next, true);
    });

    function currentPage() {
      const m = location.pathname.match(/\/page\/(\d+)/);
      if (m) return parseInt(m[1], 10);
      return parseInt(new URLSearchParams(location.search).get('paged') || '1', 10);
    }

    async function swap(layout, persist) {
      region.classList.add('is-switching');
      try {
        const data = await window.scrollToUtils.restGet('/layout', {
          layout: layout,
          paged:  currentPage(),
          sort:   region.dataset.sort || 'recent',
        });
        if (!data || data.code || !data.html) return;
        const parsed = new DOMParser().parseFromString(data.html, 'text/html');
        region.replaceChildren(...parsed.body.childNodes);
        region.dataset.layout = layout;
        syncSidebarVisibility(layout);
        if (persist) {
          try { localStorage.setItem(STORE_KEY, layout); } catch (_) {}
          const secure = location.protocol === 'https:' ? ';Secure' : '';
          document.cookie = STORE_KEY + '=' + layout + ';path=/;max-age=31536000;SameSite=Lax' + secure;
        }
      } catch (err) {
        console.error('Layout swap failed', err);
      } finally {
        region.classList.remove('is-switching');
      }
    }

    /**
     * Sync the homepage wrap's sidebar state to the current layout.
     * Rule: feed always shows the sidebar; grid honours data-sidebar-option.
     * Toggling the `scroll-page-wrap--full` modifier drops the sidebar grid
     * column and hides the <aside> via the matching CSS rule.
     */
    function syncSidebarVisibility(layout) {
      const wrap = region.closest('.scroll-page-wrap');
      if (!wrap) return;
      const optionOn = wrap.dataset.sidebarOption === '1';
      const shouldShow = layout === 'feed' || optionOn;
      wrap.classList.toggle('scroll-page-wrap--full', !shouldShow);
    }
  })();
})();
